import {
  AdministrationRoles,
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { BuildingsModel, ResourceCalendarDefault } from '../../../../../types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getListOfBuildings,
  getResourceDefaultModel,
  updateDefaultDomain,
  UserKeys,
} from '../../../../api/users-api';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { handleErrors } from '../../../../utils/errors';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../shared/analytics/module-list';
import * as analytics from '../../../../shared/analytics/analytics.json';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import dayjs from 'dayjs';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';
import UserAvatar from '../../../../shared/user-avatar';

import Box from '@mui/material/Box';
import SharedButton from '../../../../shared/buttons/shared-button';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';
import SharedTabHeader from '../../../../shared/shared-tab-header';

interface options {
  displayValue: string;
  value: string;
}

interface ShareObject {
  [key: string]: string;
}

export default function DomainDefaultsPage() {
  const { calendarRoles, changeLogRoles, loggedInUser } = useUserSettings();

  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState<
    BuildingsModel | null | undefined
  >(null);

  const { data: domainDefault } = useQuery({
    queryKey: [UserKeys.ResourceCalendarDefaults],
    queryFn: () => getResourceDefaultModel(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('domain defaults'),
    },
  });

  const {
    data: buildingsList,
    isLoading: buildingsLoading,
    isFetching: buildingsFetching,
  } = useQuery({
    queryKey: [UserKeys.BuildingsList],
    queryFn: () => getListOfBuildings(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('buildings'),
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isLoading },
    watch,
    setValue,
    getValues,
    reset,
  } = useForm<Partial<ResourceCalendarDefault>>({
    values: {
      ...domainDefault,
    },
  });

  const shares = watch('shares');
  const calSharedToDomain = watch('calSharedToDomain') || false;
  const calPublic = watch('calPublic') || false;

  useEffect(() => {
    if (!buildingsLoading && buildingsList) {
      const building = buildingsList?.find(
        (building) => building.buildingId === domainDefault?.buildingId
      );
      setSelectedBuilding(building);
    }
  }, [
    buildingsList,
    buildingsLoading,
    domainDefault?.buildingId,
    setSelectedBuilding,
  ]);

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<ResourceCalendarDefault>) =>
      updateDefaultDomain(data, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Resource calendar defaults'),
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.ResourceCalendarDefaults],
      });

      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      reset(getValues(), { keepValues: true });
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'resource calendar defaults'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.calendars.resource_calendars.update_domain_default,
        {
          module: ModuleList.modules.calendars.name,
          section: ModuleList.modules.calendars.sections,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function addSelection(email: string) {
    setValue(
      'shares',
      {
        ...shares,
        [`${email}`]: 'reader',
      },
      { shouldDirty: true }
    );
    setAutoCompleteResponse('');
  }

  const resourceCategoryOptions: options[] = [];

  if (
    isAbleToSeeUIElement(
      calendarRoles,
      CalendarRoles.CanSelectCategoryUnknown.value
    )
  ) {
    resourceCategoryOptions.push({
      displayValue: 'Unknown',
      value: 'CATEGORY_UNKNOWN',
    });
  }

  if (
    isAbleToSeeUIElement(
      calendarRoles,
      CalendarRoles.CanSelectConferenceRoom.value
    )
  ) {
    resourceCategoryOptions.push({
      displayValue: 'Conference Room',
      value: 'CONFERENCE_ROOM',
    });
  }

  if (isAbleToSeeUIElement(calendarRoles, CalendarRoles.CanSelectOther.value)) {
    resourceCategoryOptions.push({ displayValue: 'Other', value: 'OTHER' });
  }

  const roleOptions = [
    {
      displayValue: 'See Only Free/Busy',
      value: 'freeBusyReader',
    },
    {
      displayValue: 'See All Events Details',
      value: 'reader',
    },
  ];

  const onSubmit: SubmitHandler<Partial<ResourceCalendarDefault>> = (data) => {
    const isCategoryChanged =
      domainDefault?.resourceCategory !== data.resourceCategory;
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.ViewDomainDefaultResourceCalendar.value
      ) ||
      (isCategoryChanged &&
        ((isAbleToSeeUIElement(
          changeLogRoles,
          CalendarRoles.CanSelectCategoryUnknown.value
        ) &&
          data.resourceCategory === 'CATEGORY_UNKNOWN') ||
          (isAbleToSeeUIElement(
            changeLogRoles,
            CalendarRoles.CanSelectConferenceRoom.value
          ) &&
            data.resourceCategory === 'CONFERENCE_ROOM') ||
          (isAbleToSeeUIElement(
            changeLogRoles,
            CalendarRoles.CanSelectOther.value
          ) &&
            data.resourceCategory === 'OTHER')))
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate({
        ...data,
        lastUpdatingUser: loggedInUser,
        lastUpdated: dayjs(new Date()).valueOf(),
      });
    }
  };
  if (buildingsLoading || buildingsFetching) {
    return (
      <Box>
        <Card sx={{ position: 'relative', p: 4, pb: 6 }}>
          <SharedTabHeader title="Resource Calendar Defaults" />

          <Box>
            <Divider variant="middle" textAlign="left" sx={{ py: 2 }}>
              Set resource calendar default
            </Divider>
            <Stack pl={2.5}>
              <Stack flexDirection={'row'} gap={2} pb={3}>
                <Skeleton variant="rounded" height={40} width={100} />
              </Stack>
              <Stack flexDirection={'row'} gap={2} pb={3}>
                <Skeleton variant="rounded" height={40} width={400} />
                <Skeleton variant="rounded" height={40} width={400} />
              </Stack>
              <Stack flexDirection={'row'} gap={2} pb={3}>
                <Skeleton variant="rounded" height={40} width={400} />
                <Skeleton variant="rounded" height={40} width={191.5} />
                <Skeleton variant="rounded" height={40} width={191.5} />
                <Skeleton variant="rounded" height={40} width={191.5} />
              </Stack>
            </Stack>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Card
        component="form"
        sx={{ position: 'relative', p: 3, pb: 6, pt: 1 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <SharedTabHeader title="Resource Calendar Defaults" noDivider>
          <SharedButton
            variant="primary"
            disabled={!isDirty || isPending}
            loading={isPending || isLoading}
            type="submit"
          >
            Save
          </SharedButton>
        </SharedTabHeader>

        <Box>
          <Divider variant="middle" textAlign="left" sx={{ py: 1 }}>
            Set resource calendar default
          </Divider>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This sets your default selections on resource calendar creation. All
            selections can be edited on calendar creation. <br />
            Active must be checked in order to apply defaults and shares.
          </Typography>
          <Stack gap={1}>
            <Controller
              name="isActive"
              control={control}
              render={({ field: props }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      inputProps={{
                        id: 'active',
                      }}
                      {...props}
                      checked={props.value}
                      onChange={(e) => {
                        props.onChange(e.target.checked);
                      }}
                    />
                  }
                  label="Active"
                />
              )}
            />
            <Stack flexDirection={'row'} gap={2}>
              <Controller
                name="resourceCategory"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'resourceCategory',
                    }}
                    InputLabelProps={{
                      htmlFor: 'resourceCategory',
                    }}
                    select
                    label="Resource Category"
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {resourceCategoryOptions?.map((option) => (
                      <MenuItem value={option.value} key={option.value}>
                        {option.displayValue}
                      </MenuItem>
                    )) || []}
                  </TextField>
                )}
              />
              <Controller
                name="resourceType"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'resourceType',
                    }}
                    InputLabelProps={{
                      htmlFor: 'resourceType',
                    }}
                    label="Resource Type"
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
            </Stack>
            <Stack flexDirection={'row'} gap={2}>
              <Controller
                name="buildingId"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'buildingId',
                    }}
                    InputLabelProps={{
                      htmlFor: 'buildingId',
                    }}
                    label="Building"
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      const building = buildingsList?.find(
                        (building) => building.buildingId === e.target.value
                      );
                      setSelectedBuilding(building);
                      setValue('floorName', '');
                    }}
                    select
                    sx={{ width: 400 }}
                    autoComplete="off"
                  >
                    {buildingsList?.map((building) => (
                      <MenuItem
                        value={building.buildingId}
                        key={building.buildingId}
                      >
                        {building.buildingName}
                      </MenuItem>
                    )) || []}
                  </TextField>
                )}
              />
              <Controller
                name="floorName"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'floorName',
                    }}
                    InputLabelProps={{
                      htmlFor: 'floorName',
                    }}
                    select
                    label="Floor"
                    sx={{ width: 191.5 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {selectedBuilding?.floorNames.map((floor) => (
                      <MenuItem value={floor} key={floor}>
                        {floor}
                      </MenuItem>
                    )) || []}
                  </TextField>
                )}
              />
              <Controller
                name="floorSection"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'floorSection',
                    }}
                    InputLabelProps={{
                      htmlFor: 'floorSection',
                    }}
                    label="Floor Section"
                    sx={{ width: 191.5 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="capacity"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'capacity',
                    }}
                    InputLabelProps={{
                      htmlFor: 'capacity',
                    }}
                    label="Capacity"
                    type="number"
                    sx={{ width: 191.5 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
            </Stack>
          </Stack>
          <Divider variant="middle" textAlign="left" sx={{ py: 2 }}>
            Set share settings
          </Divider>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This is setting the default share users for any newly created
            calendars. This can take a few minutes to update.
          </Typography>
          <Stack width={500} gap={3}>
            <Stack gap={2}>
              <Stack flexDirection={'row'} justifyContent={'space-between'}>
                <Controller
                  name="calSharedToDomain"
                  control={control}
                  render={({ field: props }) => (
                    <FormControlLabel
                      sx={{ alignSelf: 'flex-start' }}
                      control={
                        <Checkbox
                          inputProps={{
                            id: 'calSharedToDomain',
                          }}
                          {...props}
                          checked={props.value}
                          onChange={(e) => {
                            props.onChange(e.target.checked);
                            if (!e.target.checked) {
                              setValue('calPublic', false);
                            }
                          }}
                        />
                      }
                      label="Share Calendar to Domain"
                    />
                  )}
                />
                <Controller
                  name="domainRole"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      inputProps={{
                        id: 'domainRole',
                      }}
                      InputLabelProps={{
                        htmlFor: 'domainRole',
                      }}
                      disabled={!calSharedToDomain}
                      select
                      required
                      label="Domain Role"
                      sx={{ width: 220 }}
                      value={field.value || ''}
                      onChange={field.onChange}
                    >
                      {roleOptions.map((option) => (
                        <MenuItem
                          value={option.value}
                          key={option.displayValue}
                        >
                          {option.displayValue}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Stack>
              <Stack flexDirection={'row'} justifyContent={'space-between'}>
                <Controller
                  name="calPublic"
                  control={control}
                  render={({ field: props }) => (
                    <FormControlLabel
                      sx={{ alignSelf: 'flex-start' }}
                      control={
                        <Checkbox
                          inputProps={{
                            id: 'calPublic',
                          }}
                          {...props}
                          checked={props.value}
                          onChange={(e) => {
                            props.onChange(e.target.checked);

                            if (e.target.checked) {
                              setValue('calSharedToDomain', true);
                            }
                          }}
                        />
                      }
                      label="Share Calendar to Public"
                    />
                  )}
                />
                <Controller
                  name="publicRole"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      inputProps={{
                        id: 'publicRole',
                      }}
                      InputLabelProps={{
                        htmlFor: 'publicRole',
                      }}
                      disabled={!calPublic}
                      select
                      required
                      label="Public Role"
                      sx={{ width: 220 }}
                      value={field.value || ''}
                      onChange={field.onChange}
                    >
                      {roleOptions.map((option) => (
                        <MenuItem
                          value={option.value}
                          key={option.displayValue}
                        >
                          {option.displayValue}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Stack>
            </Stack>
            <Stack>
              <Stack
                flexDirection="row"
                gap={1}
                sx={{ my: 3 }}
                justifyContent="center"
              >
                <Box sx={{ width: 450 }}>
                  <SharedAutocomplete
                    sources={['users']}
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={setAutoCompleteResponse}
                    onSelection={(value) => {
                      setAutoCompleteResponse(value);
                      if (!value) return;
                      addSelection(value);
                    }}
                  />
                </Box>
              </Stack>

              <Typography sx={{ mb: 2 }}>Shares:</Typography>
              <Paper
                sx={{
                  minWidth: 630,
                  overflow: 'hidden',
                  maxHeight: 195,
                }}
              >
                <TableContainer sx={{ minWidth: 630, maxHeight: 195 }}>
                  <Table
                    size={'small'}
                    stickyHeader
                    aria-label="searched-shares"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shares
                        ? Object.keys(shares).map((user) => {
                            return (
                              <TableRow key={user}>
                                <TableCell>
                                  <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={2}
                                  >
                                    <UserAvatar userEmail={user} />
                                    <Typography variant="body2">
                                      {user}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={1}
                                  >
                                    <TextField
                                      inputProps={{
                                        id: 'role',
                                      }}
                                      InputLabelProps={{
                                        htmlFor: 'role',
                                      }}
                                      select
                                      label="Role"
                                      size="small"
                                      value={shares[user as keyof object]}
                                      onChange={(event) => {
                                        const copy: ShareObject = {
                                          ...shares,
                                        };
                                        copy[user] = event.target.value;
                                        setValue('shares', copy, {
                                          shouldDirty: true,
                                        });
                                      }}
                                      sx={{ width: 200 }}
                                    >
                                      <MenuItem key="writer" value="writer">
                                        Editor
                                      </MenuItem>
                                      <MenuItem
                                        key="freeBusyReader"
                                        value="freeBusyReader"
                                      >
                                        Free/Busy Viewer
                                      </MenuItem>
                                      <MenuItem key="owner" value="owner">
                                        Owner
                                      </MenuItem>

                                      <MenuItem key="reader" value="reader">
                                        Viewer
                                      </MenuItem>
                                      <MenuItem
                                        key="writerWithoutPrivateAccess"
                                        value="writerWithoutPrivateAccess"
                                      >
                                        Make changes (see private events as
                                        free/busy)
                                      </MenuItem>
                                    </TextField>
                                    <Tooltip arrow title="Delete">
                                      <span>
                                        <IconButton
                                          onClick={() => {
                                            const copy = { ...shares };
                                            delete copy[user as keyof object];
                                            setValue('shares', copy, {
                                              shouldDirty: true,
                                            });
                                          }}
                                        >
                                          <Delete />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          </Stack>
        </Box>

        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            const newTemplate = {
              ...getValues(),
            } as ResourceCalendarDefault;
            mutate(newTemplate);
          }}
          mutateLoading={isPending}
        />
      </Card>
    </Box>
  );
}
